import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';

const MARGIN = { top: 10, right: 80, bottom: 10, left: 160 };
const ROW_HEIGHT = 40;
const ROW_GAP = 12;
const BAR_HEIGHT = 24;
const ACTUAL_BAR_HEIGHT = 10;

const RANGE_COLORS = ['#e5e7eb', '#d1d5db', '#9ca3af'];
const ACTUAL_COLOR = '#2563eb';
const TARGET_COLOR = '#1f2937';

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  header: {
    marginBottom: '16px',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    margin: 0,
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    margin: '4px 0 0 0',
  },
  container: {
    overflowX: 'auto',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
};

function formatValue(value, format) {
  if (value == null) return '--';
  switch (format) {
    case 'dollar':
      return `$${value.toFixed(2)}`;
    case 'percent':
      return `${value.toFixed(1)}%`;
    case 'number':
    default:
      return value.toFixed(1);
  }
}

export default function BulletChart({ data }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    if (!data || data.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const containerWidth = containerRef.current
      ? containerRef.current.clientWidth
      : 600;
    const width = Math.max(500, containerWidth);
    const totalHeight = MARGIN.top + data.length * (ROW_HEIGHT + ROW_GAP) - ROW_GAP + MARGIN.bottom;
    const innerW = width - MARGIN.left - MARGIN.right;

    svg.attr('width', width).attr('height', totalHeight);

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

    data.forEach((item, i) => {
      const rowY = i * (ROW_HEIGHT + ROW_GAP);
      const barCenterY = rowY + ROW_HEIGHT / 2;

      // Determine scale domain: 0 to max of ranges
      const maxRange = Math.max(...item.ranges, item.actual, item.target);
      const x = d3.scaleLinear()
        .domain([0, maxRange])
        .range([0, innerW]);

      const rowGroup = g.append('g')
        .attr('class', 'bullet-row')
        .style('cursor', 'pointer');

      // Draw qualitative ranges (background bands) — widest first (good), then satisfactory, then poor
      // ranges = [poor/satisfactory threshold, satisfactory/good threshold, max]
      // The widest band (full range) is the "good" zone — lightest gray
      // Medium band is "satisfactory" — medium gray
      // Narrowest band is "poor" — darkest gray
      const rangeWidths = [...item.ranges].sort((a, b) => b - a);
      rangeWidths.forEach((rangeVal, ri) => {
        rowGroup.append('rect')
          .attr('x', 0)
          .attr('y', barCenterY - BAR_HEIGHT / 2)
          .attr('width', x(rangeVal))
          .attr('height', BAR_HEIGHT)
          .attr('fill', RANGE_COLORS[ri])
          .attr('rx', 2);
      });

      // Draw actual value bar (narrower, colored)
      rowGroup.append('rect')
        .attr('x', 0)
        .attr('y', barCenterY - ACTUAL_BAR_HEIGHT / 2)
        .attr('width', x(item.actual))
        .attr('height', ACTUAL_BAR_HEIGHT)
        .attr('fill', ACTUAL_COLOR)
        .attr('rx', 1);

      // Draw target marker (thin vertical line)
      const targetX = x(item.target);
      rowGroup.append('line')
        .attr('x1', targetX)
        .attr('x2', targetX)
        .attr('y1', barCenterY - BAR_HEIGHT / 2 - 2)
        .attr('y2', barCenterY + BAR_HEIGHT / 2 + 2)
        .attr('stroke', TARGET_COLOR)
        .attr('stroke-width', 2.5);

      // Label on the left
      g.append('text')
        .attr('x', -8)
        .attr('y', barCenterY - 4)
        .attr('text-anchor', 'end')
        .attr('font-size', '12px')
        .attr('font-weight', 600)
        .attr('fill', '#1f2937')
        .text(item.label);

      // Subtitle on the left (below label)
      if (item.subtitle) {
        g.append('text')
          .attr('x', -8)
          .attr('y', barCenterY + 10)
          .attr('text-anchor', 'end')
          .attr('font-size', '10px')
          .attr('fill', '#9ca3af')
          .text(item.subtitle);
      }

      // Value on the right
      g.append('text')
        .attr('x', innerW + 8)
        .attr('y', barCenterY)
        .attr('dy', '0.35em')
        .attr('text-anchor', 'start')
        .attr('font-size', '12px')
        .attr('font-weight', 600)
        .attr('fill', ACTUAL_COLOR)
        .text(formatValue(item.actual, item.format));

      // Invisible hover target for entire row
      rowGroup.append('rect')
        .attr('x', 0)
        .attr('y', rowY)
        .attr('width', innerW)
        .attr('height', ROW_HEIGHT)
        .attr('fill', 'transparent')
        .on('mousemove', (event) => {
          setTooltip({
            x: event.clientX + 12,
            y: event.clientY - 10,
            label: item.label,
            subtitle: item.subtitle,
            actual: item.actual,
            target: item.target,
            ranges: item.ranges,
            format: item.format,
          });
        })
        .on('mouseleave', () => {
          setTooltip(null);
        });
    });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data]);

  if (!data || data.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.title}>Performance Benchmarks</div>
          <div style={styles.subtitle}>Actual values vs targets across key metrics</div>
        </div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No benchmark data available
        </div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div style={styles.title}>Performance Benchmarks</div>
        <div style={styles.subtitle}>Actual values vs targets across key metrics</div>
      </div>
      <div style={styles.container} ref={containerRef}>
        <svg ref={svgRef} />
      </div>
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {tooltip.label}
          </div>
          {tooltip.subtitle && (
            <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '6px' }}>
              {tooltip.subtitle}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: ACTUAL_COLOR }}>Actual</span>
            <span style={{ fontWeight: 600 }}>{formatValue(tooltip.actual, tooltip.format)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Target</span>
            <span style={{ fontWeight: 600 }}>{formatValue(tooltip.target, tooltip.format)}</span>
          </div>
          <div style={{ borderTop: '1px solid #f3f4f6', marginTop: '4px', paddingTop: '4px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
              <span style={{ color: '#6b7280' }}>Variance</span>
              <span
                style={{
                  fontWeight: 600,
                  color: tooltip.actual >= tooltip.target ? '#16a34a' : '#dc2626',
                }}
              >
                {tooltip.actual >= tooltip.target ? '+' : ''}
                {formatValue(tooltip.actual - tooltip.target, tooltip.format)}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
              <span style={{ color: '#6b7280' }}>Ranges</span>
              <span style={{ fontWeight: 600 }}>
                {tooltip.ranges.map((r) => formatValue(r, tooltip.format)).join(' / ')}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
